import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { UserDropDown } from '@/types/user.types';

// Define the WorkflowTemplate interface
export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  event_type?: string;
  entity_type?: string;
  priority?: number;
  is_system_template?: boolean;
  version?: string;
  created_by: string;
  created_by_detail?: UserDropDown;
  created_at: string;
  updated_at: string;
  updated_by: string;
  parent_template: string;
  company?: string;
}

// Pagination interface for workflow templates
export interface WorkflowTemplatePaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: WorkflowTemplate[];
}

// Define types for request and response
export interface WorkflowTemplateCreateInput {
  name: string;
  description?: string;
  event_type?: string;
  entity_type?: string;
  priority?: number;
  is_system_template?: boolean;
  version?: string;
  conditions?: Array<{
    field_name: string;
    operator: string;
    value: string | number | boolean;
    condition_type: 'dropdown' | 'text' | 'number' | 'date';
    order: number;
  }>;
}

// Priority values from backend enum
export enum WorkflowPriority {
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
  CRITICAL = 4
}

export interface WorkflowTemplateUpdateInput extends Partial<WorkflowTemplateCreateInput> {
  updateReason?: string;
  conditions?: Array<{
    field_name: string;
    operator: string;
    value: string | number | boolean;
    condition_type: 'dropdown' | 'text' | 'number' | 'date';
    order: number;
  }>;
}

export interface WorkflowTemplateFilter {
  search?: string;
  entity_type?: string;
  event_type?: string;
  /** 1-based page number (backend pagination) */
  page?: number;
  /** Page size (backend uses query param `limit`) */
  limit?: number;
  /** Ordering field, e.g. 'name', '-created_at', 'updated_at' (must be one of: name, created_at, updated_at) */
  ordering?: string;
  [key: string]: unknown;
}

const WORKFLOW_TEMPLATE_FILTER_ALIASES: Record<string, string> = {
  event_type__icontains: 'event_type',
  event_type__exact: 'event_type',
  entity_type__icontains: 'entity_type',
  entity_type__exact: 'entity_type',
  priority__icontains: 'priority',
  priority__exact: 'priority',
};

// Create API
export const workflowTemplateApi = createApi({
  reducerPath: 'workflowTemplateApi',
  baseQuery: createExtendedTokenBaseQuery('workflows/'),
  tagTypes: ['WorkflowTemplate'],
  endpoints: (builder) => ({
    getWorkflowTemplates: builder.query<WorkflowTemplatePaginatedResponse, WorkflowTemplateFilter | void>({
      query: (filter = {}) => {
        const params = new URLSearchParams();
        if (filter) {
          Object.entries(filter).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              params.append(WORKFLOW_TEMPLATE_FILTER_ALIASES[key] ?? key, String(value));
            }
          });
        }
        return { url: 'workflow-templates/', params };
      },
      providesTags: ['WorkflowTemplate'],
    }),
    getWorkflowTemplateById: builder.query<WorkflowTemplate, string>({
      query: (id) => `workflow-templates/${id}/`,
      providesTags: (result, error, id) => [{ type: 'WorkflowTemplate', id }],
    }),
    createWorkflowTemplate: builder.mutation<WorkflowTemplate, WorkflowTemplateCreateInput>({
      query: (template) => ({
        url: 'workflow-templates/',
        method: 'POST',
        body: template,
      }),
      invalidatesTags: ['WorkflowTemplate'],
    }),
    updateWorkflowTemplate: builder.mutation<WorkflowTemplate, { id: string; template: WorkflowTemplateUpdateInput }>({
      query: ({ id, template }) => ({
        url: `workflow-templates/${id}/`,
        method: 'PUT',
        body: template,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'WorkflowTemplate', id }, 
        'WorkflowTemplate'
      ],
    }),
    deleteWorkflowTemplate: builder.mutation<void, string>({
      query: (id) => ({
        url: `workflow-templates/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['WorkflowTemplate'],
    }),
    activateWorkflowTemplate: builder.mutation<WorkflowTemplate, string>({
      query: (id) => ({
        url: `workflow-templates/${id}/activate/`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'WorkflowTemplate', id }, 
        'WorkflowTemplate'
      ],
    }),
    deactivateWorkflowTemplate: builder.mutation<WorkflowTemplate, string>({
      query: (id) => ({
        url: `workflow-templates/${id}/deactivate/`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'WorkflowTemplate', id }, 
        'WorkflowTemplate'
      ],
    }),
    // Priority management
    updateWorkflowTemplatePriority: builder.mutation<WorkflowTemplate, { id: string; priority: number }>({
      query: ({ id, priority }) => ({
        url: `workflow-templates/${id}/priority/`,
        method: 'PATCH',
        body: { priority },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'WorkflowTemplate', id },
        'WorkflowTemplate'
      ],
    }),
    
    // Bulk update priorities
    bulkUpdatePriorities: builder.mutation<void, { priorities: { id: string; priority: number }[] }>({
      query: (data) => ({
        url: 'workflow-templates/bulk-priority-update/',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['WorkflowTemplate'],
    }),
    
    // Get priority statistics
    getPriorityStats: builder.query<{ count: number }, void>({
      query: () => 'workflow-templates/priority-stats/',
      providesTags: ['WorkflowTemplate'],
    }),
  }),
});

// Export hooks
export const {
  useGetWorkflowTemplatesQuery,
  useGetWorkflowTemplateByIdQuery,
  useCreateWorkflowTemplateMutation,
  useUpdateWorkflowTemplateMutation,
  useDeleteWorkflowTemplateMutation,
  useActivateWorkflowTemplateMutation,
  useDeactivateWorkflowTemplateMutation,
  useUpdateWorkflowTemplatePriorityMutation,
  useBulkUpdatePrioritiesMutation,
  useGetPriorityStatsQuery,
} = workflowTemplateApi;
